/* import __COLOCATED_TEMPLATE__ from './select-all-checkbox-manager.hbs'; */
/**

Provides a "Select All" global checkbox and checkboxes for each list item. You can use the yielded checkboxes in whatever UI you like.

```example
@title Table Usage
@controller-properties
{
  options: [
    { label: 'Matt', checked: false },
    { label: 'Wright', checked: true },
    { label: 'Rulz', checked: false }
  ],

  actions: {
    yourOnChangeAction(selectedOpions, changeType, changedOption) {
      switch(changeType) {
        case SINGLE:
          // `changedOption` represents the option that was clicked
        case ALL:
          // select all was clicked
        case NONE:
          // select none was clicked
      }
    }
  }
}

@hbs
{{#select-all-checkbox-manager tagName="table"
                               options=options
                               onChange=(action "yourOnChangeAction")
                               as |checkbox-manager|}}
  <thead>
    <tr>
      <th>
        {{checkbox-manager.selectAllCheckbox label="Select All"}}
      </th>
    </tr>
  </thead>

  {{#checkbox-manager.list tagName="tbody" as |row option|}}
    <tr>
      <td>
        {{row.checkbox label=option.label}}
      </td>
    </tr>
  {{/checkbox-manager.list}}
{{/select-all-checkbox-manager}}
```

```example
@title Hella Custom Usage
@controller-properties
{
  options: [
    { label: 'Matt', checked: false },
    { label: 'Wright', checked: true },
    { label: 'Rulz', checked: false }
  ]
}

@hbs
<div style="border: dashed 20px pink; overflow: hidden;">
  {{#select-all-checkbox-manager options=options
                                 as |checkbox-manager|}}
    <div style="padding: 20px; background: orange;">
      {{checkbox-manager.selectAllCheckbox label="Select All"}}
    </div>

    {{#checkbox-manager.list as |row option|}}
      <div style="float: left; width: 33.3%; padding: 20px; background: yellow;">
        {{row.checkbox label=option.label}}
      </div>
    {{/checkbox-manager.list}}
  {{/select-all-checkbox-manager}}
</div>
```

@param {String} [listComponent='select-all-checkbox-manager/list'] - Optional component to use for rendering the options list
@param {Array} [options] - An array of options. Each option should have an `checked` attribute for initializing and managing selection state. An `isDisabled` attribute will be respected when selecting all, if present.
@param {Action} [onChange] - An action that triggers when the selections change. `onChange` will always be passed the full list of currently selected options, as well as a change type.
                             The change type will be one of `ALL`, `NONE`, or `SINGLE`. When the change type is `SINGLE`, the changed option will be provided as the 3rd argument.
*/

import { set, computed } from '@ember/object';
import { A } from '@ember/array';
import Component from '@ember/component';
import { CHECKBOX_STATE } from '@square/glass-ui/components/sq-checkbox';
import defaultListComponent from '@square/glass-ui/components/select-all-checkbox-manager/list';
export const CHECKBOX_MANAGER_CHANGE_TYPES = {
  ALL: 'ALL',
  NONE: 'NONE',
  SINGLE: 'SINGLE'
};

// eslint-disable-next-line ember/no-classic-classes
export default Component.extend({
  options: null,
  isSelectedAll: false,
  isSelectedNone: false,
  selectAllCheckedState: computed('isSelectedAll', 'isSelectedNone', function () {
    if (!this.get('isSelectedAll') && !this.get('isSelectedNone')) {
      return CHECKBOX_STATE.INDETERMINATE;
    } else if (this.get('isSelectedAll')) {
      return CHECKBOX_STATE.CHECKED;
    } else {
      return CHECKBOX_STATE.UNCHECKED;
    }
  }),
  listComponent: defaultListComponent,
  didReceiveAttrs() {
    this._super();
    // Ensure options are an Ember Array
    this.set('options', A(this.get('options')));
    const selectedOptions = this.get('options').filterBy('checked');
    this.updateSelectAllState(selectedOptions);
  },
  updateSelectAllState(selectedOptions) {
    const isSelectedNone = selectedOptions.length === 0;
    const isSelectedAll = !isSelectedNone && selectedOptions.length === this.get('options').length;
    this.set('isSelectedAll', isSelectedAll);
    this.set('isSelectedNone', isSelectedNone);
  },
  actions: {
    selectAllDidChange(checked) {
      const options = this.get('options');
      for (const option of options.rejectBy('disabled')) {
        set(option, 'checked', checked);
      }
      const selectedOptions = options.filterBy('checked');
      this.updateSelectAllState(selectedOptions);
      if (this.onChange) {
        const changeType = checked ? CHECKBOX_MANAGER_CHANGE_TYPES.ALL : CHECKBOX_MANAGER_CHANGE_TYPES.NONE;
        this.onChange(selectedOptions, changeType);
      }
    },
    optionDidChange(checked, option) {
      set(option, 'checked', checked);
      const selectedOptions = this.get('options').filterBy('checked');
      this.updateSelectAllState(selectedOptions);
      if (this.onChange) {
        this.onChange(selectedOptions, CHECKBOX_MANAGER_CHANGE_TYPES.SINGLE, option);
      }
    }
  }
});